import { useCallback, useEffect, useState } from 'react';
import { FileText, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { cn } from '../../../lib/utils';
import { usePermission } from '../../rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import { billingApi, saveBlob, type BillingDocument } from '../../../lib/api/billing';
import { formatCents, formatIsoDate } from '../../../lib/billing';

const BELEGE = P.buchhaltung.buchhaltungsbelege_und_dokumente;

interface Props {
  contractAccountId: number;
  accountNumber: string;
}

/**
 * Belege eines Vertragskontos (ADR-0003, Phase 7): Sammelbeleg je Monat, Download als PDF
 * und Storno. Ein Beleg wird nie geändert — nach dem Storno kann der Monat neu belegt werden.
 */
export default function DocumentsPanel({ contractAccountId, accountNumber }: Props) {
  const canView = usePermission(BELEGE.finanzdokumente_ansehen);
  const canCreate = usePermission(BELEGE.finanzdokumente_erstellen);
  const canEdit = usePermission(BELEGE.finanzdokumente_bearbeiten);
  const [documents, setDocuments] = useState<BillingDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState<BillingDocument | null>(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const response = await billingApi.listDocuments({ contractAccountId, limit: 24 });
      setDocuments(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Belege konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [canView, contractAccountId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!canView) return null;

  const download = async (document: BillingDocument) => {
    setBusy(true);
    try {
      const file = await billingApi.downloadDocument(document.id, document.fileName);
      saveBlob(file.blob, file.filename);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Beleg konnte nicht geladen werden');
    } finally {
      setBusy(false);
    }
  };

  const createForLastMonth = async () => {
    setBusy(true);
    try {
      const summary = await billingApi.runDocuments({ contractAccountId });
      toast.success(
        summary.documents > 0
          ? `Beleg ${summary.numbers.join(', ')} erstellt`
          : 'Keine unbelegte Buchung im Vormonat',
      );
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Beleg konnte nicht erstellt werden');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-md border bg-muted/20 p-3">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-sm font-medium">Belege</h4>
        {canCreate && (
          <Button variant="outline" size="sm" onClick={() => void createForLastMonth()} disabled={busy}>
            <FileText className="mr-2 h-4 w-4" />
            Beleg für den Vormonat
          </Button>
        )}
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Belege werden geladen …</p>
      ) : documents.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Noch kein Beleg. Der Belegjob erstellt am Monatsanfang einen Sammelbeleg.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border bg-background">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Beleg</TableHead>
                <TableHead>Zeitraum</TableHead>
                <TableHead className="text-right">Betrag</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map((document) => (
                <TableRow
                  key={document.id}
                  className={cn(document.status === 'cancelled' && 'text-muted-foreground')}
                >
                  <TableCell>
                    <div className="font-medium tabular-nums">{document.number}</div>
                    <div className="text-xs text-muted-foreground">
                      {document.kindLabel} · {formatIsoDate(document.documentDate)}
                      {document.status === 'cancelled' && (
                        <Badge variant="secondary" size="sm" className="ml-2">
                          Storniert
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm tabular-nums">
                    {formatIsoDate(document.periodStart)} – {formatIsoDate(document.periodEnd)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCents(document.totalCents)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void download(document)}
                        disabled={busy}
                        aria-label={`Beleg ${document.number} herunterladen`}
                      >
                        PDF
                      </Button>
                      {canEdit && document.status === 'issued' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setReason('');
                            setCancelling(document);
                          }}
                          disabled={busy}
                        >
                          <Undo2 className="mr-1 h-4 w-4" />
                          Stornieren
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {cancelling && (
        <Dialog open onOpenChange={(open) => !open && setCancelling(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Beleg stornieren?</DialogTitle>
              <DialogDescription>
                {cancelling.kindLabel} {cancelling.number} für {accountNumber}. Die Nummer bleibt
                vergeben; die Buchungen können danach neu belegt werden.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="document-cancel-reason">Grund</Label>
              <Input
                id="document-cancel-reason"
                maxLength={200}
                placeholder="z. B. falscher Empfänger"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelling(null)} disabled={busy}>
                Abbrechen
              </Button>
              <Button
                variant="destructive"
                disabled={busy || !reason.trim()}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await billingApi.cancelDocument(cancelling.id, reason.trim());
                    toast.success('Beleg storniert');
                    setCancelling(null);
                    await load();
                  } catch (error) {
                    toast.error(error instanceof Error ? error.message : 'Storno fehlgeschlagen');
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Stornieren
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
